import { FC, useMemo, useRef } from 'react';
import { Textarea } from 'src/components/Textarea';
import { Option } from 'src/components/SelectBox';
import { CommunicationTiming } from 'src/types/automation';
import StepCard from '../step-card/StepCard';
import type { StepMetrics } from '../step-card/StepCardHeader';
import { CommunicationChannel } from 'src/services/campaigns/campaigns.interface';
import { CalendarBlank, ChatDots } from '@phosphor-icons/react';
import StepTimingControls from '../step-card/StepTimingControls';
import AddVariableButton from 'src/components/AddVariableButton';
import { extractVariables } from 'src/utils/extractVariables';

type SmsStepCardProps = {
  stepNumber: number;
  body: string;
  onBodyChange: (value: string) => void;
  communicationTiming?: CommunicationTiming;
  onCommunicationTimingChange?: (timing: CommunicationTiming) => void;
  timeWindowOptions?: Option[];
  onTimeWindowChange?: (option: Option) => void;
  selectedTimeWindowOption?: Option;
  onTitleChange?: (value: string) => void;
  metrics?: StepMetrics;
  addStepAbove?: () => void;
  addStepBelow?: () => void;
  deleteStep?: () => void;
  hideTimeWindow?: boolean;
  disabled?: boolean;
};

export const SmsStepCard: FC<SmsStepCardProps> = ({
  stepNumber,
  body,
  onBodyChange,
  onTitleChange,
  communicationTiming,
  onCommunicationTimingChange,
  timeWindowOptions,
  selectedTimeWindowOption,
  onTimeWindowChange,
  metrics,
  addStepAbove,
  addStepBelow,
  deleteStep,
  hideTimeWindow = false,
  disabled = false,
}) => {
  const isError = !body.trim();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const variables = useMemo(() => extractVariables(body || ''), [body]);

  const summary = (
    <div className="flex flex-wrap items-center gap-6">
      {isError && (
        <div className="inline-flex items-center gap-2 py-2 px-3 rounded-xl bg-error/10">
          <ChatDots className="h-5 w-5" />
          <span>Message still needs content</span>
        </div>
      )}
      {body.trim() && (
        <div className="inline-flex items-center gap-2 min-w-0">
          <ChatDots className="h-5 w-5 text-neutral-500" />
          <span className="text-neutral-700 truncate max-w-[25rem]">
            {body}
          </span>
        </div>
      )}
      <div className="inline-flex items-center gap-2">
        <CalendarBlank className="h-5 w-5 text-neutral-500" />
        <span className="text-neutral-700">
          {communicationTiming === 'earliest'
            ? 'At the earliest eligible time'
            : selectedTimeWindowOption?.label}
        </span>
      </div>
    </div>
  );
  return (
    <StepCard
      stepNumber={stepNumber}
      titleDefaultValue={{
        value: CommunicationChannel.SMS,
        label: 'SMS',
      }}
      titleOnChange={onTitleChange}
      metrics={metrics}
      summary={summary}
      error={isError}
      onAddStepAbove={addStepAbove}
      onAddStepBelow={addStepBelow}
      onDeleteStep={deleteStep}
      disabled={disabled}
    >
      <div className="w-full sm:w-full flex-shrink-0 flex flex-col gap-y-6">
        <div className="w-full rounded-xl border border-neutral-200 bg-neutral-0 p-4">
          <Textarea
            placeholder="Draft SMS message..."
            rows={6}
            value={body}
            onChange={(e) => onBodyChange(e.target.value)}
            variant="text"
            color="default"
            className="w-full bg-transparent hover:bg-transparent focus:bg-transparent shadow-none"
            ref={textareaRef}
            disabled={disabled}
          />
          <AddVariableButton
            textareaRef={textareaRef}
            variables={variables}
            position="top-left"
            disabled={disabled}
          />
        </div>
        {/* TODO: Add back in when we have a way to set the time window for calls
        {!hideTimeWindow && (
          <StepTimingControls
            label="When to send message"
            timing={communicationTiming}
            onTimingChange={onCommunicationTimingChange}
            timeWindowOptions={timeWindowOptions}
            selectedTimeWindowOption={selectedTimeWindowOption}
            onTimeWindowChange={onTimeWindowChange}
            disabled={disabled}
          />
        )} */}
      </div>
    </StepCard>
  );
};

export default SmsStepCard;
